import { FC, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { SeoLink } from 'dibs-elements/exports/SeoLink';

import { getByDocCreator } from '../helpers/getByDocCreator';
import { trackRecentSalesModuleBrowseSimilarLinkClick } from './recentSalesTracking';

import {
    PdpSharedRecentSalesCopy_item$key,
    PdpSharedRecentSalesCopy_item$data,
} from './__generated__/PdpSharedRecentSalesCopy_item.graphql';

import styles from './styles/PdpSharedRecentSalesCopy.scss';

type Props = {
    item: PdpSharedRecentSalesCopy_item$key;
    highlightRareFind?: boolean;
    discoverSimilarItems?: boolean;
};

const getItemTypeForCopyText = ({
    iconicDesign,
    creators,
    classification,
}: PdpSharedRecentSalesCopy_item$data): ReactNode | string => {
    if (iconicDesign) {
        const iconicDesignName = iconicDesign.displayName;
        return iconicDesignName ? (
            <FormattedMessage
                id="pdpRecentSales.iconicDesignPlural"
                defaultMessage="similar <a>{design}s</a>"
                values={{
                    design: iconicDesignName,
                    a: (content: ReactNode[]) => (
                        <SeoLink
                            linkData={{
                                path: iconicDesign.browsePageLinkData.path || '',
                                isLinkable: iconicDesign.browsePageLinkData.isLinkable,
                            }}
                        >
                            {content}
                        </SeoLink>
                    ),
                }}
            />
        ) : (
            ''
        );
    }

    const lowestCategory = (classification?.categories || []).find(c => c?.precedence === 1);
    const lowestCategoryName = lowestCategory?.category?.name || '';

    const creatorName = getByDocCreator(creators);
    if (creatorName && creatorName.toLowerCase() !== 'unknown') {
        return (
            <FormattedMessage
                id="pdpRecentSales.categoryWithCreator"
                defaultMessage="{category} by {creator}"
                values={{ category: lowestCategoryName, creator: creatorName }}
            />
        );
    }

    return (
        <FormattedMessage
            id="pdpRecentSales.category"
            defaultMessage="similar {category}"
            values={{ category: lowestCategoryName }}
        />
    );
};

const itemFragment = graphql`
    fragment PdpSharedRecentSalesCopy_item on Item {
        serviceId
        similarRecentlySoldItemsPrices(currency: $currency) {
            priceRange
        }
        iconicDesign {
            displayName
            browsePageLinkData {
                isLinkable
                path
            }
        }
        creators {
            attribution
            creator {
                name
                displayName
            }
        }
        classification {
            categories {
                precedence
                category {
                    name
                    browseUrl
                }
            }
        }
    }
`;

export const PdpSharedRecentSalesCopy: FC<Props> = ({
    item: itemRef,
    highlightRareFind = false,
    discoverSimilarItems = false,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { priceRange } = item.similarRecentlySoldItemsPrices || {};

    if (!priceRange) {
        return null;
    }

    return (
        <FormattedMessage
            id="PdpSharedRecentSalesCopy.copyContent"
            defaultMessage={`{range, select,
                OUT_OF_RANGE_LOW {This item is priced <green>lower</green> than recent sales of {type} on 1stDibs. <a>Discover similar items</a>}
                LOWER_RANGE {This item is priced in the <green>lower range</green> of recent sales of {type} on 1stDibs. <a>Discover similar items</a>}
                NEAR_MEDIAN_LOW {This item is priced near the median of recent sales of {type} on 1stDibs. <a>Discover similar items</a>}
                MEDIAN {This item's price is equal to the median recent sale price of {type} on 1stDibs. <a>Discover similar items</a>}
                NEAR_MEDIAN_HIGH {This item is priced near the median of recent sales of {type} on 1stDibs. <a>Discover similar items</a>}
                HIGHER_RANGE {This item is priced in the higher range of recent sales of {type} on 1stDibs. <a>Discover similar items</a>}
                OUT_OF_RANGE_HIGH {This item is priced higher than recent sales of {type} on 1stDibs. <a>Discover similar items</a>}
                other {}
            }`}
            values={{
                range: priceRange,
                type: getItemTypeForCopyText(item),
                green: (content: ReactNode[]) =>
                    highlightRareFind ? (
                        <span className={styles.greenText}>{content}</span>
                    ) : (
                        content
                    ),
                a: (content: ReactNode[]) => {
                    if (!discoverSimilarItems) {
                        return null;
                    }

                    return (
                        <SeoLink
                            linkData={{
                                path: `/similar/?item=${item.serviceId}`,
                            }}
                            target="_blank"
                            onClick={() => trackRecentSalesModuleBrowseSimilarLinkClick(priceRange)}
                        >
                            {content}
                        </SeoLink>
                    );
                },
            }}
        />
    );
};
